<script lang="ts">
  import { onMount, onDestroy } from 'svelte';

  type Testimonial = {
    quote: string;
    name: string;
    // Sublabel reads "{role}, {company}" when a role is set, else just the
    // company. Only the company is linked (to `link`).
    role?: string;
    company: string;
    link: string;
    // Nav indicator: either a logo image (`logo`) or a text glyph (`glyph`,
    // e.g. '@' for individual researchers without a logo).
    logo?: string;
    glyph?: string;
    label: string;
  };

  // One entry per slide, in display order. Drives both the carousel and the
  // logo navigation below it.
  const testimonials: Testimonial[] = [
    {
      quote:
        'I’ve used Zelix’s product. I’ve used a couple of open-source products and yours, and Test Machine continues to outperform the other products that I’ve used. Only good things to say about it.',
      name: 'Lee',
      role: 'CEO',
      company: '33Labs',
      link: 'https://www.33labs.ai/',
      logo: '/logos/33labs.png',
      label: '33Labs'
    },
    {
      quote:
        '[TestMachine] stood out because it achieved 100% accuracy on all tokens, with no false positives or negatives, even catching human errors in our own reviews.',
      name: 'Noama Samreen',
      role: 'Blockchain Security Engineer',
      company: 'Coinbase',
      link: 'https://www.coinbase.com/',
      logo: '/logos/coinbase.png',
      label: 'Coinbase'
    },
    {
      quote:
        'I really am genuinely impressed with the depth on complex setups. This feels like a real step up from traditional static + fuzz combos. The fact that it only surfaces proven execution paths (not theoretical flags) is exactly what the space needs.',
      name: 'Luca Sami',
      company: 'Independent Security Researcher',
      link: 'https://x.com/0x4008',
      logo: '/logos/luca.png',
      label: 'Luca Sami'
    },
    {
      quote: 'It’s kinda straightforward, fast, and cheap.',
      name: 'Evgeny',
      role: 'CEO',
      company: 'Pessimistic',
      link: 'https://pessimistic.io/',
      logo: '/logos/pessimistic.png',
      label: 'Pessimistic'
    },
    {
      quote:
        'Overall, I’m really impressed by the tool, mainly because it provides very high signal. As an auditor, I hate spending 15 minutes reading an AI finding only to realize it hallucinated a line of code and I’ve wasted time.',
      name: '@LuxLode',
      company: 'Independent Security Researcher',
      link: 'https://x.com/LuxLode',
      logo: '/logos/luxlode.png',
      label: 'LuxLode'
    },
    {
      quote:
        'I like what you are building and looking forward to future advancements. I am pretty sure this will turn out useful for many builders and make my work more effective when I get to work on codebases which are already in good shape.',
      name: 'Mario Poneder',
      company: 'Independent Security Researcher',
      link: 'https://x.com/MarioPoneder',
      logo: '/logos/mario.png',
      label: 'Mario Poneder'
    },
    {
      quote:
        'We have found the tool fairly useful. I don’t think it found anything meaningful, which gave us some comfort. We have been using a few systems to check the code. It did pick up two changes which we did implement.',
      name: 'Matthew Hall',
      role: 'Co-Founder & Chief Financial Product Officer',
      company: 'Axiym',
      link: 'https://axiym.io/',
      logo: '/logos/axiym.svg',
      label: 'Axiym'
    }
  ];

  let track: HTMLDivElement | undefined = $state();
  let current = $state(0);
  let autoTimer: ReturnType<typeof setInterval> | undefined;

  const total = testimonials.length;

  function goTo(i: number) {
    current = ((i % total) + total) % total;
    if (track) {
      track.style.transform = `translateX(-${current * 100}%)`;
    }
  }

  function next() { goTo(current + 1); }

  function resetAuto() {
    if (autoTimer) clearInterval(autoTimer);
    autoTimer = setInterval(next, 9000);
  }

  function handleDot(i: number) {
    goTo(i);
    resetAuto();
  }

  onMount(() => {
    resetAuto();
  });

  onDestroy(() => {
    if (autoTimer) clearInterval(autoTimer);
  });
</script>

<section class="social-proof" id="social-proof">
  <div class="container">
    <div class="sp-carousel" data-animate>
      <div class="sp-track" bind:this={track}>
        {#each testimonials as t}
          <div class="sp-slide">
            <blockquote class="sp-quote">&ldquo;{t.quote}&rdquo;</blockquote>
            <div class="sp-attribution">
              <span class="sp-name">{t.name}</span>
              <span class="sp-role">
                {#if t.role}{t.role + ', '}{/if}<a
                  href={t.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="sp-company-link">{t.company}</a
                >
              </span>
            </div>
          </div>
        {/each}
      </div>
    </div>
    <div class="sp-logos" data-animate>
      {#each testimonials as t, i}
        <button
          class="sp-logo"
          class:sp-logo-active={current === i}
          onclick={() => handleDot(i)}
          aria-label="Show testimonial from {t.label}"
          aria-pressed={current === i}
        >
          {#if t.glyph}
            <span class="sp-logo-glyph" aria-hidden="true">{t.glyph}</span>
          {:else}
            <img src={t.logo} alt={t.label} loading="lazy" />
          {/if}
        </button>
      {/each}
    </div>
  </div>
</section>

<style>
  .social-proof {
    /* Uniform section gap above; the section below owns its own gap. */
    padding: var(--section-py) 0 0;
    background: var(--bg);
  }

  .sp-carousel {
    max-width: 760px;
    margin: 0 auto;
    overflow: hidden;
  }

  .sp-track {
    display: flex;
    align-items: stretch;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;
  }

  .sp-slide {
    min-width: 100%;
    box-sizing: border-box;
    padding: 0 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .sp-quote {
    font-family: var(--font-display);
    font-size: clamp(1.125rem, 2.4vw, 1.5rem);
    font-weight: 400;
    line-height: 1.6;
    letter-spacing: -0.01em;
    color: var(--fg);
    margin: 0 0 28px;
    max-width: 60ch;
    text-wrap: balance;
  }

  .sp-attribution {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .sp-name {
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--accent);
  }

  .sp-role {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--fg-muted);
  }

  /* Only the company portion of the sublabel is a link. */
  .sp-company-link {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
  }
  .sp-company-link:hover {
    color: var(--accent);
  }

  /* Source logos double as the carousel navigation. Inactive logos are dimmed
     and desaturated; the active source's logo shows in full brand color. */
  .sp-logos {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: clamp(20px, 4vw, 44px);
    margin-top: 2.5rem;
  }

  .sp-logo {
    display: inline-flex;
    align-items: center;
    border: 0;
    background: none;
    padding: 4px;
    cursor: pointer;
    opacity: 0.4;
    transition: opacity 0.3s;
  }
  .sp-logo:hover {
    opacity: 0.7;
  }
  .sp-logo-active {
    opacity: 1;
  }
  .sp-logo img {
    display: block;
    height: 32px;
    width: auto;
    max-width: 150px;
    object-fit: contain;
  }
  /* Stand-in for sources without a logo (individual researchers). Sized to
     match the 32px logo height so the nav row stays aligned. */
  .sp-logo-glyph {
    display: block;
    height: 32px;
    line-height: 32px;
    font-family: var(--font-mono);
    font-size: 26px;
    font-weight: 500;
    color: var(--fg);
  }

  @media (max-width: 768px) {
    .sp-slide {
      padding: 0;
    }
  }
</style>
